import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axiosConfig';

// Interface cho dữ liệu thống kê
interface StatsData {
  totalProducts: number;
  totalNews: number;
  newContactsThisMonth: number;
  qrActivationsToday: number;
}

// === HÀM getAuthToken ĐÃ ĐƯỢC SỬA LẠI CHO ĐÚNG ===
const getAuthToken = (): string | null => {
  const userString = sessionStorage.getItem('user');
  if (userString) {
    try {
      const userData = JSON.parse(userString);
      return userData.accessToken || null;
    } catch (error) {
      console.error("Lỗi khi đọc dữ liệu người dùng:", error);
      return null;
    }
  }
  return null;
};

// Dữ liệu cho các thẻ chức năng quản lý
const managementFeatures = [
  { title: 'Quản lý Sản phẩm', description: 'Chỉnh sửa, thêm hoặc xóa các sản phẩm.', link: '/admin/products-management', icon: ( <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg> ), bgColor: 'bg-blue-500' },
  { title: 'Quản lý Tin tức', description: 'Tạo và cập nhật các bài viết, hướng dẫn.', link: '/admin/news-management', icon: ( <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6m-1 8h.01" /></svg> ), bgColor: 'bg-green-500' },
  { title: 'Quản lý Thiết bị & QR', description: 'Quản lý loại thiết bị và các mã sê-ri.', link: '/admin/devices', icon: ( <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg> ), bgColor: 'bg-yellow-500' },
  { title: 'Quản lý Khách hàng', description: 'Xem và quản lý thông tin khách hàng.', link: '/admin/customers', icon: ( <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg> ), bgColor: 'bg-red-500' },
];

const Dashboard = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = getAuthToken();
        const { data } = await api.get('/dashboard/stats', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(data);
      } catch (error) {
        console.error('Lỗi khi tải dữ liệu thống kê:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const displayStats = stats ? [
    { name: 'Tổng Sản phẩm', value: stats.totalProducts, unit: 'sản phẩm' },
    { name: 'Số Bài viết', value: stats.totalNews, unit: 'bài viết' },
    { name: 'Khách hàng mới / tháng', value: stats.newContactsThisMonth, unit: 'khách hàng' },
    { name: 'Lượt kích hoạt QR / ngày', value: stats.qrActivationsToday, unit: 'lượt' },
  ] : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-100 min-h-screen">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Bảng điều khiển</h1>
        <p className="text-gray-500 mt-1">Chào mừng trở lại, Admin!</p>
      </header>

      {/* Phần thống kê nhanh */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))
        ) : (
          displayStats.map((stat, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md">
              <p className="text-sm font-medium text-gray-500">{stat.name}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.unit}</p>
            </div>
          ))
        )}
      </div>

      {/* Phần chức năng quản lý */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Chức năng quản lý</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {managementFeatures.map((feature, index) => (
            <Link to={feature.link || '#'} key={index} className="group block">
                <div className="flex items-start p-6 bg-white rounded-lg shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className={`flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-lg ${feature.bgColor}`}>
                        {feature.icon}
                    </div>
                    <div className="ml-6">
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {feature.title}
                        </h3>
                        <p className="mt-2 text-gray-600">
                            {feature.description}
                        </p>
                    </div>
                </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;